import { z } from "zod";

import { fetchWithAuth } from "@/lib/api";
import { parseApiResponse } from "@/lib/api-validation";
import { watchlistSchema, watchlistItemSchema } from "@/schemas/user.schema";

import type { Watchlist, WatchlistItem } from "@/schemas";

import { API_BASE } from "@/config/api";

// Response schemas
const watchlistsResponseSchema = z.object({
  watchlists: z.array(watchlistSchema),
});

const watchlistResponseSchema = z.object({
  watchlist: watchlistSchema,
});

const itemsResponseSchema = z.object({
  items: z.array(watchlistItemSchema),
});

const itemResponseSchema = z.object({
  item: watchlistItemSchema,
});

const watchlistWithItemsSchema = watchlistSchema.extend({
  items: z.array(watchlistItemSchema).nullable(),
});

const watchlistsWithItemsResponseSchema = z.object({
  watchlists: z.array(watchlistWithItemsSchema),
});

export interface WatchlistWithItems extends Watchlist {
  items: WatchlistItem[];
}

async function parseError(response: Response, fallback: string): Promise<string> {
  const data = await response.json().catch(() => ({}));
  return data.error || fallback;
}

export const WatchlistService = {
  async list(): Promise<Watchlist[]> {
    const response = await fetchWithAuth(`${API_BASE}/watchlists`);
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to fetch watchlists"));
    }
    const data = await response.json();
    const validated = parseApiResponse(
      watchlistsResponseSchema,
      data,
      "WatchlistService.list"
    );
    return validated.watchlists ?? [];
  },

  async create(name: string): Promise<Watchlist> {
    const response = await fetchWithAuth(`${API_BASE}/watchlists`, {
      method: "POST",
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to create watchlist"));
    }
    const data = await response.json();
    const validated = parseApiResponse(
      watchlistResponseSchema,
      data,
      "WatchlistService.create"
    );
    return validated.watchlist;
  },

  async listWithItems(): Promise<WatchlistWithItems[]> {
    const response = await fetchWithAuth(`${API_BASE}/watchlists/items`);
    if (!response.ok) {
      throw new Error(
        await parseError(response, "Failed to fetch watchlists with items")
      );
    }
    const data = await response.json();
    const validated = parseApiResponse(
      watchlistsWithItemsResponseSchema,
      data,
      "WatchlistService.listWithItems"
    );
    return (validated.watchlists ?? []).map((wl) => ({
      ...wl,
      items: wl.items ?? [],
    }));
  },

  async rename(watchlistId: string, name: string): Promise<Watchlist> {
    const response = await fetchWithAuth(
      `${API_BASE}/watchlists/${watchlistId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }
    );
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to rename watchlist"));
    }
    const data = await response.json();
    const validated = parseApiResponse(
      watchlistResponseSchema,
      data,
      "WatchlistService.rename"
    );
    return validated.watchlist;
  },

  async remove(watchlistId: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE}/watchlists/${watchlistId}`,
      {
        method: "DELETE",
      }
    );
    if (!response.ok && response.status !== 204) {
      throw new Error(await parseError(response, "Failed to delete watchlist"));
    }
  },

  async items(watchlistId: string): Promise<WatchlistItem[]> {
    const response = await fetchWithAuth(
      `${API_BASE}/watchlists/${watchlistId}/items`
    );
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to fetch watchlist items"));
    }
    const data = await response.json();
    const validated = parseApiResponse(
      itemsResponseSchema,
      data,
      "WatchlistService.items"
    );
    return validated.items ?? [];
  },

  async addItem(
    watchlistId: string,
    payload: { asset_id: string; note?: string; target_price?: number | null }
  ): Promise<WatchlistItem> {
    const response = await fetchWithAuth(
      `${API_BASE}/watchlists/${watchlistId}/items`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      throw new Error(
        response.status === 409
          ? "Asset already in watchlist"
          : await parseError(response, "Failed to add item")
      );
    }
    const data = await response.json();
    const validated = parseApiResponse(
      itemResponseSchema,
      data,
      "WatchlistService.addItem"
    );
    return validated.item;
  },

  async updateItem(
    watchlistId: string,
    itemId: string,
    payload: { note?: string | null; target_price?: number | null }
  ): Promise<WatchlistItem> {
    const response = await fetchWithAuth(
      `${API_BASE}/watchlists/${watchlistId}/items/${itemId}`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to update item"));
    }
    const data = await response.json();
    const validated = parseApiResponse(
      itemResponseSchema,
      data,
      "WatchlistService.updateItem"
    );
    return validated.item;
  },

  async removeItem(watchlistId: string, itemId: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE}/watchlists/${watchlistId}/items/${itemId}`,
      {
        method: "DELETE",
      }
    );
    if (!response.ok && response.status !== 204) {
      throw new Error(await parseError(response, "Failed to delete item"));
    }
  },

  async reorderItems(watchlistId: string, itemIds: string[]): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE}/watchlists/${watchlistId}/items/reorder`,
      {
        method: "PUT",
        body: JSON.stringify({ item_ids: itemIds }),
      }
    );
    if (!response.ok) {
      throw new Error(await parseError(response, "Failed to reorder items"));
    }
  },

  async moveItem(
    watchlistId: string,
    itemId: string,
    targetWatchlistId: string
  ): Promise<WatchlistItem> {
    const response = await fetchWithAuth(
      `${API_BASE}/watchlists/${watchlistId}/items/${itemId}/move`,
      {
        method: "POST",
        body: JSON.stringify({ target_watchlist_id: targetWatchlistId }),
      }
    );
    if (!response.ok) {
      throw new Error(
        response.status === 409
          ? "Asset already in target watchlist"
          : await parseError(response, "Failed to move item")
      );
    }
    const data = await response.json();
    const validated = parseApiResponse(
      itemResponseSchema,
      data,
      "WatchlistService.moveItem"
    );
    return validated.item;
  },
};
